import { AnimatePresence } from 'framer-motion';
import { lazy, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import MainLayout from '../../layouts/MainLayout';
import { routeLoaders } from '../../utils/routePreload';
import LoadingScreen from '../LoadingScreen';

const DashboardLayout = lazy(routeLoaders['/dashboard-layout']);
const LandingPage = lazy(routeLoaders['/']);
const ContactPage = lazy(routeLoaders['/contact-us']);
const DataPage = lazy(routeLoaders['/explore-data']);
const GrowWithGooglePage = lazy(routeLoaders['/grow-with-google']);
const PrivacyPolicyPage = lazy(routeLoaders['/privacy-policy']);
const ResourcesPage = lazy(routeLoaders['/resources']);
const GlobePage = lazy(routeLoaders['/asset-map']);
const AdvancedManufacturing = lazy(routeLoaders['/advanced-manufacturing']);
const ArchitectureEngineering = lazy(routeLoaders['/architecture-engineering']);
const Cybersecurity = lazy(routeLoaders['/cybersecurity']);
const DigitalHealthPage = lazy(routeLoaders['/digital-health']);
const FinancialServices = lazy(routeLoaders['/financial-services-insurance']);
const HumanLifeSciencesPage = lazy(routeLoaders['/human-life-sciences']);
const PlantAnimalSciencesPage = lazy(routeLoaders['/plant-&-animal-sciences']);
const TechnologyPage = lazy(routeLoaders['/technology']);
const CyberRangePage = lazy(routeLoaders['/heartland-cyber-range']);
const DataSpherePage = lazy(routeLoaders['/datasphere']);
const SecureSpaceCampusPage = lazy(routeLoaders['/secure-space-campus']);
const TalentSpherePage = lazy(routeLoaders['/talentsphere']);
const OurApproachPage = lazy(routeLoaders['/our-approach']);
const CyberRangeLandingPage = lazy(routeLoaders['/defend-tomorrow']);
const Operation287 = lazy(routeLoaders['/operation287']);
const OnePage = lazy(routeLoaders['/OnePage']);
const ImageHost = lazy(routeLoaders['/image-host']);
const CmsPageView = lazy(routeLoaders['/:slug']);
const CmsPreviewPage = lazy(routeLoaders['/cms-preview']);
const DashboardPage = lazy(routeLoaders['/dashboard']);
const GlobeDashboardPage = lazy(routeLoaders['/dashboard/asset-map']);
const LoginPage = lazy(routeLoaders['/admin/login']);
const LogoutPage = lazy(routeLoaders['/admin/logout']);
const RegisterPage = lazy(routeLoaders['/admin/register']);
const ResourceDashboardPage = lazy(routeLoaders['/dashboard/resources']);
const UserManagementPage = lazy(routeLoaders['/dashboard/users']);
const AccountSecurityPage = lazy(routeLoaders['/dashboard/account']);
const PageBuilderPage = lazy(routeLoaders['/dashboard/pages']);
const ResourceTemplatePage = lazy(routeLoaders['/dashboard/resource-templates']);
const ContentDeployPage = lazy(routeLoaders['/dashboard/content-deploy']);
const GitMergePage = lazy(routeLoaders['/dashboard/git-merge']);
const ConstantsPage = lazy(routeLoaders['/dashboard/constants']);
const MediaLibraryPage = lazy(routeLoaders['/dashboard/media']);
const BannerContactPage = lazy(routeLoaders['/dashboard/banner']);
const ActivityLogPage = lazy(routeLoaders['/dashboard/activity']);

function AnimatedRoutes() {
    const location = useLocation();

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [location]);

    return (
        <AnimatePresence mode="wait">
            <Suspense fallback={<LoadingScreen />} key={location.pathname}>
                <Routes location={location} key={location.pathname}>
                    {location.pathname.startsWith('/defendtomorrow') ? null : (
                        <Route path="*" element={<Navigate to="/" />} />
                    )}
                    <Route path="/cms-preview" element={<CmsPreviewPage />} />
                    <Route element={<MainLayout />}>
                        <Route path="/" element={<LandingPage />} />
                        <Route path="/asset-map" element={<GlobePage />} />
                        <Route path="/contact-us" element={<ContactPage />} />
                        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                        <Route path="/resources" element={<ResourcesPage />} />
                        <Route path="/advanced-manufacturing" element={<AdvancedManufacturing />} />
                        <Route path="/cybersecurity" element={<Cybersecurity />} />
                        <Route path="/architecture-engineering" element={<ArchitectureEngineering />} />
                        <Route path="/financial-services-insurance" element={<FinancialServices />} />
                        <Route path="/human-life-sciences" element={<HumanLifeSciencesPage />} />
                        <Route path="/plant-&-animal-sciences" element={<PlantAnimalSciencesPage />} />
                        <Route path="/technology" element={<TechnologyPage />} />
                        <Route path="/digital-health" element={<DigitalHealthPage />} />
                        <Route path="/datasphere" element={<DataSpherePage />} />
                        <Route path="/heartland-cyber-range" element={<CyberRangePage />} />
                        <Route path="/secure-space-campus" element={<SecureSpaceCampusPage />} />
                        <Route path="/talentsphere" element={<TalentSpherePage />} />
                        <Route path="/grow-with-google" element={<GrowWithGooglePage />} />
                        <Route path="/our-approach" element={<OurApproachPage />} />
                        <Route path="/defend-tomorrow" element={<CyberRangeLandingPage />} />
                        <Route path="/defendtomorrow" element={<Navigate to="/defend-tomorrow" />} />
                        <Route path="/operation287" element={<Operation287 />} />
                        <Route path="/pages/:slug" element={<CmsPageView />} />
                        <Route path="/:slug" element={<CmsPageView />} />
                    </Route>

                    <Route path="/explore-data/:state/:industry" element={<DataPage />} />
                    <Route path="/explore-data/:state/" element={<DataPage />} />

                    <Route path="/admin" element={<Navigate to="/admin/login" />} />
                    <Route path="/admin/login" element={<LoginPage />} />
                    <Route path="/admin/register" element={<RegisterPage />} />
                    <Route path="/admin/logout" element={<LogoutPage />} />

                    <Route path="/OnePage" element={<OnePage />} />
                    <Route path="/image-host" element={<ImageHost />} />

                    <Route element={<DashboardLayout />}>
                        <Route path="/dashboard" element={<DashboardPage />} />
                        <Route path="/dashboard/asset-map" element={<GlobeDashboardPage />} />
                        <Route path="/dashboard/resources" element={<ResourceDashboardPage />} />
                        <Route path="/dashboard/cms" element={<Navigate to="/dashboard/pages" replace />} />
                        <Route path="/dashboard/banner" element={<BannerContactPage />} />
                        <Route path="/dashboard/pages" element={<PageBuilderPage />} />
                        <Route path="/dashboard/resource-templates" element={<ResourceTemplatePage />} />
                        <Route path="/dashboard/content-deploy" element={<ContentDeployPage />} />
                        <Route path="/dashboard/git-merge" element={<GitMergePage />} />
                        <Route path="/dashboard/constants" element={<ConstantsPage />} />
                        <Route path="/dashboard/media" element={<MediaLibraryPage />} />
                        <Route path="/dashboard/users" element={<UserManagementPage />} />
                        <Route path="/dashboard/account" element={<AccountSecurityPage />} />
                        <Route path="/dashboard/activity" element={<ActivityLogPage />} />
                    </Route>
                </Routes>
            </Suspense>
        </AnimatePresence>
    );
}

export default AnimatedRoutes;
